import React, { useState } from 'react'
import { MicOff, X } from 'lucide-react'
import { useCallStore } from '@/stores/callStore'
import { SafeAreaTop } from '@/components/ui/SafeAreaTop'

/**
 * Persistent banner shown when Twilio Device (or an explicit getUserMedia
 * probe) fails with a permission-denied error. "Retry" re-requests mic access.
 * Blush tint (error severity); the Retry action is the primary button. Dismissible with X.
 * Unlike the toast path, this does NOT auto-dismiss.
 */
export function MicPermissionBanner(): React.ReactElement | null {
  const micPermissionDenied = useCallStore((s) => s.micPermissionDenied)
  const retry = useCallStore((s) => s.retryMicPermission)
  const [retrying, setRetrying] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  if (!micPermissionDenied || dismissed) return null

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await retry()
    } finally {
      setRetrying(false)
    }
  }

  return (
    <SafeAreaTop
      extra="calc(3.5rem + 12px)"
      className="fixed top-0 left-0 right-0 z-[70] pointer-events-none animate-fade-up"
    >
    <div
      role="alert"
      aria-live="assertive"
      className="mx-auto max-w-md w-[calc(100%-24px)] pointer-events-auto"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 14px',
          borderRadius: 'var(--r-md)',
          background: 'rgb(var(--blush-rgb) / 0.12)',
          border: '0.5px solid rgb(var(--blush-rgb) / 0.35)',
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.6)',
          backdropFilter: 'blur(32px) saturate(180%)',
          WebkitBackdropFilter: 'blur(32px) saturate(180%)',
        }}
      >
        {/* Icon chip */}
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-sm)',
            background: 'rgb(var(--blush-rgb) / 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MicOff style={{ width: 15, height: 15, color: 'var(--midnight-blush)' }} />
        </div>

        {/* Message */}
        <p style={{ fontSize: 13, color: 'rgb(var(--fg-rgb) / 0.80)', flex: 1, lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600, color: 'var(--midnight-blush)' }}>Microphone blocked</span>
          {' \u2014 calls won\u2019t work until you allow mic access.'}
        </p>

        {/* Retry pill */}
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying}
          style={{
            height: 32,
            padding: '0 12px',
            borderRadius: 'var(--r-pill)',
            border: '0.5px solid transparent',
            background: 'var(--accent)',
            color: 'var(--accent-ink)',
            fontSize: 12,
            fontWeight: 600,
            cursor: retrying ? 'not-allowed' : 'pointer',
            opacity: retrying ? 0.6 : 1,
            touchAction: 'manipulation',
            flexShrink: 0,
          }}
        >
          {retrying ? 'Checking…' : 'Retry'}
        </button>

        {/* Dismiss X */}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss microphone banner"
          style={{
            width: 24,
            height: 24,
            borderRadius: 'var(--r-pill)',
            border: '0.5px solid rgb(var(--fg-rgb) / 0.08)',
            background: 'rgb(var(--fg-rgb) / 0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--midnight-text3)',
            flexShrink: 0,
          }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>
      </div>
    </div>
    </SafeAreaTop>
  )
}
